import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "className", "components", "ContainerProps", "disabledDays", "edsContext", "endYear", "highlightToday", "id", "defaultMonth", "maxDate", "minDate", "onChange", "onDayClick", "showOutsideDays", "startYear", "value", "rangeValues", "variant", "withMonthYearSelect"];
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { DayPicker as RdpDayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { withStyles } from '@ellucian/react-design-system/core/styles';
import { ChevronLeft, ChevronRight, ChevronDown } from '@ellucian/ds-icons/lib';
import styles from './styles/styles';
import LOCALES from './internal/Locales';
import CustomDayContent from './internal/DayContent';
import { getDefaultMaxDate, getDefaultMinDate } from './internal/DateUtils';
import { withEDSContext } from '../EDSContext/EDSContext';
export var VARIANTS = {
  DATE_PICKER: 'date-picker',
  DATE_RANGE_PICKER: 'date-range-picker'
};
export var FIRST_SELECTABLE_DAY = '.rdp-day_selected:not(.rdp-day_disabled):not(.rdp-day_outside)';
export var FIRST_DAY_FOR_SELECTION = '.rdp-day:not(.rdp-day_disabled)';
export var FIRST_DAY_IN_MONTH = '.rdp-day:not(.rdp-day_disabled):not(.rdp-day_outside)';
export var CURRENT_DATE = '.rdp-day_today:not(.rdp-day_disabled):not(.rdp-day_outside)';
export var SELECTED_END_DATE = '.rdp-day_range_end:not(.rdp-day_disabled):not(.rdp-day_outside)';

/**
 * Use `StandaloneDatePicker` to display a calendar that allows date selection.
 *
 * @done true
 * @updated v7.10.0
 * @versionAdded v7.0.0
 * @examples
 *  StandaloneDatePickerExample
 */
var StandaloneDatePicker = function StandaloneDatePicker(props) {
  var _cn;
  var classes = props.classes,
    className = props.className,
    components = props.components,
    ContainerProps = props.ContainerProps,
    disabledDays = props.disabledDays,
    edsContext = props.edsContext,
    endYear = props.endYear,
    highlightToday = props.highlightToday,
    id = props.id,
    defaultMonthProp = props.defaultMonth,
    maxDateProp = props.maxDate,
    minDateProp = props.minDate,
    onChange = props.onChange,
    onDayClick = props.onDayClick,
    showOutsideDays = props.showOutsideDays,
    startYear = props.startYear,
    value = props.value,
    rangeValues = props.rangeValues,
    variant = props.variant,
    withMonthYearSelect = props.withMonthYearSelect,
    rest = _objectWithoutPropertiesLoose(props, _excluded);

  // if locale is sent in with minus sign, switch to underscore
  // this matches up with a locale from the LOCALES.js constant file
  var locale = edsContext.locale;
  var customLabels = {
    previousMonth: edsContext.formatMessage('component.StandaloneDatePicker.previousMonth'),
    nextMonth: edsContext.formatMessage('component.StandaloneDatePicker.nextMonth'),
    // default day label repeats the weekday when using screen readers
    labelDay: function labelDay(day) {
      return format(day, 'PPP', {
        locale: LOCALES[locale]
      });
    }
  };

  // We want to mainly support the fromDate (minDate) and the toDate (maxDate)
  // When using captionLayout="dropdown" we NEED to send in a fromDate/toDate or startYear/endYear, or else it will not render as dropdowns
  // DatePicker supports startYear, endYear, minDate and maxDate
  // DateRangePicker supports ONLY minDate and maxDate... since it does not support the captionLayout='dropdown'

  // If minDate or maxDate is sent in, then use them as start and end
  var minDate = minDateProp || getDefaultMinDate();
  var maxDate = maxDateProp || getDefaultMaxDate();

  // If startYear is sent in, use that for the minDate (01/01/xx)
  if (startYear) {
    minDate = new Date(startYear, 0, 1); // set to January 1st of the startYear
  }
  // If endYear is sent in, use that for the maxDate (12/31/xx)
  if (endYear) {
    maxDate = new Date(endYear, 11, 31); // set to December 31st of the endYear
  }
  var dir = edsContext.dir;

  /**
   * Internal modifiers that DayPicker offers.
   * Modifiers are: disabled, hidden, outside, range_end, range_middle, range_start, selected, today
   * This results in days having the class names of: ".rdp-day_range_end" or ".rdp-day_today"
   * The modifiers class name follows the BEM standard
   * Disabled are hidden via the aria-disabled property
   * Hidden days are not rendered at all (but the tablecell element is, for the sake of spacing)
   */

  var handleOnDayClick = useCallback(function (day, selectedDay, activeModifiers, e) {
    if (activeModifiers === void 0) {
      activeModifiers = {};
    }
    // Disable users being able to click or keyboard navigate select the day
    if (activeModifiers.disabled) {
      return;
    }
    if (onDayClick) {
      onDayClick(day, selectedDay, activeModifiers, e);
    }
    if (onChange) {
      onChange(day, activeModifiers, e);
    }
  }, [onChange, onDayClick]);

  // if in a StandaloneDP or DatePicker, the defaultMonth will be the month of the value input, else the month provided by the prop
  var defaultMonth = variant === VARIANTS.DATE_PICKER && value instanceof Date ? value : defaultMonthProp;
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: cn(classes.root, className, (_cn = {}, _cn[classes.highlightToday] = highlightToday, _cn))
  }, ContainerProps), /*#__PURE__*/React.createElement(RdpDayPicker, _extends({
    mode: variant === VARIANTS.DATE_PICKER ? 'single' : 'range',
    selected: variant === VARIANTS.DATE_RANGE_PICKER ? rangeValues : value,
    onSelect: handleOnDayClick,
    defaultMonth: defaultMonth,
    fromDate: minDate,
    toDate: maxDate,
    showOutsideDays: showOutsideDays,
    captionLayout: withMonthYearSelect ? 'dropdown' : 'buttons',
    components: _extends({
      IconRight: ChevronRight,
      IconLeft: ChevronLeft,
      IconDropdown: ChevronDown,
      DayContent: CustomDayContent
    }, components),
    disabled: disabledDays,
    locale: LOCALES[locale],
    labels: customLabels,
    dir: dir
  }, rest)));
};
StandaloneDatePicker.propTypes = {
  /**
   * V7 -> V8 Prop name changes
   * | OLD -----------------  | NEW ------------- |
   * | showWeekNumbers        | showWeekNumber    |
   * | initialMonth           | defaultMonth      |
   * | canChangeMonth         | disableNavigation |
   * | selectedDays           | selected          |
   * | disabledDays           | disabled          |
   * | onWeekClick            | onWeekNumberClick |
   * |                        | onSelect (NEW)    |
   * | startYear              | fromYear          |
   * | endYear                | toYear            |
   *
   *
   * REMOVED PROPS FROM V7 (not available in V8)
   * todayButton -> removed, and they have an example of how to implement this in v8
   * DayPickerInput -> Use useInput hook
   * months -> used for custom localization, we won't use this since we rely on date-fns localization
   * weekdaysShort -> used for custom localization, we won't use this since we rely on date-fns localization
   * weekdaysLong -> used for custom localization, we won't use this since we rely on date-fns localization
   * enableOutsideDaysClick -> removed, we can implement this if this becomes a requirement
   * tabIndex -> not available
   * containerProps -> not available
   * onBlur -> not available
   * onFocus -> not available
   * onKeyDown -> not available
   * onDayMouseDown -> not available
   * onDayMouseUp -> not available
   * onCaptionClick  -> not available
   * onTodayButtonClick -> not available
   */

  /**
   * The HTML id to apply to the calendar.
   */
  id: PropTypes.string,
  /**
   * @ignore
   *
   * This function fetches resource string translations. It comes from the withEDSContext HOC.
   */
  edsContext: PropTypes.object,
  /**
   * A map of components used to create the layout.
   *
   * https://react-day-picker.js.org/api/interfaces/CustomComponents
   */
  components: PropTypes.object,
  /**
   * Props assigned to the parent div
   */
  ContainerProps: PropTypes.object,
  /**
   * When set to true, we will highligh today's date with our custom styles. When set to false, today
   * will render the same as ordinary days.
   */
  highlightToday: PropTypes.bool,
  /**
   * Class overrides.
   */
  classes: PropTypes.object,
  /**
   * If `true`, the next/previous buttons are replaced with month/year dropdowns.
   *
   * The year dropdown will be constrained to the start/end years of the minimum and maximum dates of the `DatePicker` as a whole.
   * See the prop documentation for `startYear`/`endYear` and `minDate`/`maxDate` for details.
   *
   * e.g. `startYear={2020}` will translate to 01/01/2020 and `endYear={2025}` will translate to 12/31/2025 being the last date available
   */
  withMonthYearSelect: PropTypes.bool,
  /**
   * The CSS class to add to the container of the element.
   */
  className: PropTypes.string,
  /**
   * The value of the input represented via `Date` or string of a date. Use this prop for controlled implementations.
   */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /**
   * @ignore
   * The values of the date range input represented via an array of `Date` or string of a date, or an object. Used for the internal implementation of DateRangePicker.
   */
  rangeValues: PropTypes.oneOfType([PropTypes.arrayOf(Date), PropTypes.arrayOf(String), PropTypes.object]),
  /**
   * The default month to display in the calendar when no date value is provided.
   */
  defaultMonth: PropTypes.instanceOf(Date),
  /**
   * Apply the `disabled` modifier to the matching days.
   */
  disabledDays: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.arrayOf(Date), PropTypes.func, PropTypes.object]),
  /**
   * Callback fired when the value is changed. This function sends back a `Date`: (date) => setDates(date).
   * If no days are selected, it will pass `null`. Use this prop for controlled implementations.
   */
  onChange: PropTypes.func,
  /**
   * The minimum year possible to select. If set, the minimum date will be January 1st, `startYear`.
   *
   * Note: `startYear` and `minDate` are mutually exclusive. If both are specified, only `startYear` will be honored.
   */
  startYear: PropTypes.number,
  /**
   * The maximum year possible to select. If set, the maximum date will be December 31st, `endYear`.
   *
   * Note: `endYear` and `maxDate` are mutually exclusive. If both are specified, only `endYear` will be honored.
   */
  endYear: PropTypes.number,
  /**
   * The maximum date possible to select. The selected date must fall in between the `minDate` and `maxDate`.
   *
   * Default is December 31st, 10 years in the future.
   *
   * Note: `maxDate` and `endYear` are mutually exclusive. If both are specified, only `endYear` will be honored.
   */
  maxDate: PropTypes.instanceOf(Date),
  /**
   * The minimum date possible to select. The selected date must fall in between the `minDate` and `maxDate`.
   *
   * Default is January 1st, 20 years in the past.
   *
   * Note: `minDate` and `startYear` are mutually exclusive. If both are specified, only `startYear` will be honored.
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * @ignore
   *
   * Add modifiers to the matching days.
   *
   * https://react-day-picker.js.org/api/types/DayModifiers
   *
   * For internal use only
   */
  modifiers: PropTypes.object,
  /**
   * @ignore
   *
   * Change the class name for the day matching the modifiers.
   *
   * https://react-day-picker.js.org/api/types/ModifiersClassNames
   *
   * For internal use only
   */
  modifiersClassNames: PropTypes.object,
  /**
   * @ignore
   *
   * Change the inline style for the day matching the modifiers.
   *
   * https://react-day-picker.js.org/api/types/ModifiersStyles
   *
   * For internal use only
   */
  modifiersStyles: PropTypes.object,
  /**
   * @ignore
   *
   * The number of displayed months. Defaults to `1`.
   *
   * For internal use only
   */
  numberOfMonths: PropTypes.number,
  /**
   * @ignore
   *
   * Function called when a day receives a blur event.
   *
   * https://react-day-picker.js.org/api/types/DayFocusEventHandler
   *
   * For internal use only
   */
  onDayBlur: PropTypes.func,
  /**
   * @ignore
   *
   * Event handler when the user clicks on a day cell.
   *
   * e.g.
   * `(day: date, modifiers: Object, e: SyntheticEvent) ⇒ void`
   *
   * For internal use only.
   */
  onDayClick: PropTypes.func,
  /**
   * @ignore
   *
   * Function called when a day receives a focus event.
   *
   * https://react-day-picker.js.org/api/types/DayFocusEventHandler
   *
   * For internal use only
   */
  onDayFocus: PropTypes.func,
  /**
   * @ignore
   *
   * Function called when a day receives a key down event.
   *
   * https://react-day-picker.js.org/api/types/DayKeyboardEventHandler
   *
   * For internal use only
   */
  onDayKeyDown: PropTypes.func,
  /**
   * @ignore
   *
   * Function called when a day receives a touch end event.
   *
   * https://react-day-picker.js.org/api/types/DayTouchEventHandler
   *
   * For internal use only
   */
  onDayTouchEnd: PropTypes.func,
  /**
   * Event fired when the user navigates between months.
   *
   * Signature: `(month: Date) => void`
   */
  onMonthChange: PropTypes.func,
  /**
   * @ignore
   *
   * Function called when a day receives a touch start event.
   *
   * https://react-day-picker.js.org/api/types/DayTouchEventHandler
   *
   * For internal use only
   */
  onDayTouchStart: PropTypes.func,
  /**
   * Display the days falling outside the current month.
   */
  showOutsideDays: PropTypes.bool,
  /**
   * Apply the selected modifier to the matching days.
   *
   * https://react-day-picker.js.org/api/types/Matcher
  */
  selectedDays: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.arrayOf(Date), PropTypes.func, PropTypes.object]),
  /**
   * Style to apply to the container element.
   */
  style: PropTypes.object,
  /**
   * @ignore
   *
   * Change the inline styles for each UIElement.
   *
   * https://react-day-picker.js.org/api/types/StyledElement
   */
  styles: PropTypes.object,
  /**
   * @ignore
   *
   * Selection modes supported by DayPicker.
   *  - single: use DayPicker to select single days.
   *  - multiple: allow selecting multiple days.
   *  - range: use DayPicker to select a range of days
   *  - default: disable the built-in selection behavior. Customize what is selected by using onDayClick.
   *
   * For internal use only
   */
  mode: PropTypes.oneOf(['single', 'multiple', 'range', 'default']),
  /**
   * @ignore
   *
   * Event fired when a day is selected.
   *
   * single
   *
   * For internal use only
   */
  onSelect: PropTypes.any,
  /**
   * @ignore
   *
   * Variant to help decide which mode to set the DayPicker component in.
   *
   * For internal use only.
   */
  variant: PropTypes.oneOf([VARIANTS.DATE_PICKER, VARIANTS.DATE_RANGE_PICKER])
};
StandaloneDatePicker.defaultProps = {
  defaultMonth: new Date(),
  variant: VARIANTS.DATE_PICKER,
  highlightToday: true,
  numberOfMonths: 1,
  mode: 'single'
};
export default withEDSContext(withStyles(styles)(StandaloneDatePicker));